import { apiClient } from '../utils/apiClient'
import {
  Reading,
  Employee,
  Attendance,
  AttendanceConfig,
  AttendanceSummary,
  FuelEntry,
  FuelStore,
  LastVehicleFuelEntry,
  Maintenance,
  LastMeterReading,
  Vehicle,
  Trip,
  VehicleEvent,
  MovePrecheck,
} from '../types'

interface EmployeesResponse {
  results: Array<Employee>
}
interface AssignedVehiclesResponse {
  vehicleIds: Array<string>
  employeeIds: Array<string>
}

interface AttendanceResponse {
  results: Array<Attendance>
  totalCount: number
  page: number
  limit?: number
  summary?: AttendanceSummary
}

interface PresentEmployeesResponse {
  results: {
    employees: Array<{
      employeeId: string
      status: string
      adminFirstName: string
      adminLastName: string
    }>
  }
}

interface FuelEntriesResponse {
  results: Array<FuelEntry>
  totalCount: number
  page: number
  limit: number
}

interface FuelStoreResponse {
  results: Array<FuelStore>
}

interface LastFuelEntryResponse extends LastVehicleFuelEntry {}

interface MaintenancesResponse {
  results: Array<Maintenance>
  totalCount: number
  page: number
}

interface LastMeterReadingsResponse {
  results: Array<LastMeterReading>
}

interface VehiclesResponse {
  results: Array<Vehicle>
}

interface VehicleParams {
  status?: string
  isNewVehicle?: boolean
}

interface TripsResponse {
  results: Array<Trip>
}

export interface FuelStoreEntry {
  actionOwner: string
  adminId: string
  fuelStoreId: string
  companyId: string
  description: string
  sourceFuelStoreName: string
  destinationFuelStoreName: string
  quantity: number
  transactionType: string
  adminFirstName: string
  adminLastName: string
  createdAt: string
  updatedAt: string
  _id: string
}

interface FuelStoreEntriesResponse {
  results: Array<FuelStoreEntry>
  page: number
  limit: number
  totalCount: number
}

// The field app never lists INACTIVE vehicles. Every picker goes through this
// call, so the ACTIVE default lives here (same shape as fetchAllEmployees).
// Pass an explicit status to override.
export function fetchAllVehicles({ status = 'ACTIVE', isNewVehicle }: VehicleParams) {
  return apiClient.get<VehiclesResponse>(`/vehicles`, {
    params: {
      limit: 999,
      page: 1,
      status,
      isNewVehicle,
    },
  })
}

export function fetchAllEmployees({ params = {} }: { params: any }) {
  return apiClient.get<EmployeesResponse>(`/employees`, {
    params: {
      limit: 999,
      page: 1,
      status: 'ACTIVE',
      ...params,
    },
  })
}

export function createMeterReading({ body }: { body: any }) {
  return apiClient.post(`/readings`, { ...body })
}

export function updateMeterReading({
  body,
  readingId,
  type,
}: {
  body: any
  readingId: string
  type: string
}) {
  return apiClient.patch(
    `/readings/${readingId}`,
    { ...body },
    { params: { type: type } }
  )
}

// Fetch current assigned employees and vehicles
export function fetchAssignedAssets() {
  return apiClient.get<AssignedVehiclesResponse>(`/readings/assigned`)
}

export function createAttendance({ body }: { body: any }) {
  return apiClient.post(`/attendances`, { ...body })
}

// Fetch attendances (defaults to today when no dates are passed)
export function fetchAttendance({
  page,
  limit,
  startDate,
  endDate,
  status,
}: {
  page?: number
  limit?: number
  startDate?: string
  endDate?: string
  status?: string
}) {
  return apiClient.get<AttendanceResponse>(`/attendances`, {
    params: {
      page,
      limit,
      startDate,
      endDate,
      status,
    },
  })
}

// The company's attendance config (shifts, windows, geo, enforcement).
// Field supervisors may read it (right getAttendanceConfig); writes are
// admin-only in admin-web.
export function fetchAttendanceConfig() {
  return apiClient.get<AttendanceConfig>(`/attendance-config`)
}

// Fetch employees present today
export function fetchEmployeesPresentToday() {
  return apiClient.get<PresentEmployeesResponse>(`/attendances/present-today`)
}

// Fetch fuel entries (backend defaults to last 24h when no dates are passed)
export function fetchFuelEntries({
  page,
  limit,
  startDate,
  endDate,
}: {
  page?: number
  limit?: number
  startDate?: string
  endDate?: string
} = {}) {
  return apiClient.get<FuelEntriesResponse>(`/fuel/entries`, {
    params: {
      type: ['ADD', 'TRANSFER_TO_STORE'],
      page,
      limit,
      startDate,
      endDate,
    },
  })
}

export function fetchFuelStoreEntries(params: {
  page?: number
  limit?: number
  startDate?: string
  endDate?: string
  transactionType?: string
}) {
  return apiClient.get<FuelStoreEntriesResponse>(`/fuel/stores/entries`, {
    params,
  })
}

export function fetchLastFuelEntryOfVehicle({
  vehicleId,
}: {
  vehicleId: string
}) {
  return apiClient.get<LastFuelEntryResponse>(
    `/fuel/entries/latest?vehicleId=${vehicleId}`
  )
}

export function createFuelEntry({ body }: { body: any }) {
  return apiClient.post(`/fuel/entries`, { ...body })
}

export function fetchFuelHistoryOfVehicle({
  vehicleId,
}: {
  vehicleId: string
}) {
  return apiClient.get<Array<FuelEntry>>(`/fuel/history?vehicleId=${vehicleId}`)
}

export function fetchFuelStores() {
  return apiClient.get<FuelStoreResponse>(`/fuel/stores`, {
    params: { status: 'ACTIVE' },
  })
}

/////  Maintenances /////

export function createMaintenance({ body }: { body: any }) {
  return apiClient.post(`/maintenances`, body)
}

export function updateMaintenance({
  body,
  maintenanceId,
  updateType,
}: {
  body: any
  maintenanceId: string
  updateType?: string
}) {
  return apiClient.patch(
    `/maintenances/${maintenanceId}?updateType=${updateType}`,
    { ...body }
  )
}

export function fetchMaintenances({
  startDate,
  endDate,
  page,
  vehicleId,
  employeeId,
}: {
  startDate?: string
  endDate?: string
  page: number
  vehicleId?: string
  employeeId?: string
}) {
  return apiClient.get<MaintenancesResponse>(`/maintenances`, {
    params: {
      startDate,
      endDate,
      page,
      vehicleId,
      employeeId,
    },
  })
}

export function fetchMaintenanceById({
  maintenanceId,
}: {
  maintenanceId: string
}) {
  return apiClient.get<Maintenance>(`/maintenances/${maintenanceId}`)
}

export function fetchLastMeterReadings({
  vehicleIds,
}: {
  vehicleIds: Array<string>
}) {
  return apiClient.get<LastMeterReadingsResponse>(`/readings/latest`, {
    params: {
      vehicleIds: vehicleIds.join(','),
    },
  })
}

export function fetchReadingById({ readingId }: { readingId: string }) {
  return apiClient.get<Reading>(`/readings/${readingId}`)
}

// Upload

export function uploadAssets({
  body,
  cdnFolderName,
}: {
  body: any
  cdnFolderName?: string
}) {
  return apiClient.post(`/upload?cdnFolderName=${cdnFolderName}`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// Files (S3 signed URLs)

export function getSignedUrls(keys: string[]) {
  return apiClient.post<{ urls: string[]; expiresIn: number }>(
    '/files/signed-urls',
    { keys }
  )
}

// Trips Management

export function createTrip({ body }: { body: any }) {
  return apiClient.post(`/trips`, body)
}

export function getTrips({ readingId }: { readingId: string }) {
  return apiClient.get<TripsResponse>(`/trips`, { params: { readingId } })
}

export function updateTrip({ tripId, body }: { tripId: string; body: any }) {
  return apiClient.patch(`/trips/${tripId}`, body)
}

export function getLatestTripForVehicle({
  vehicleId,
  workOrderId,
}: {
  vehicleId: string
  workOrderId: string
}) {
  return apiClient.get<Trip>(`/trips/vehicle/${vehicleId}/latest`, {
    params: { workOrderId },
  })
}

// D11 F1: rotation checklist. Without ruleId the server aggregates every
// active rotation group for the day (supervisors cannot list rules).
export interface ContinuationCandidate {
  attendanceId: string
  employeeId: string
  firstName: string | null
  middleName: string | null
  lastName: string | null
  companyEmployeeId: string | null
  jobProfile: string | null
  marked: boolean
  markedBy: string | null
  markedAt: string | null
}

export interface ContinuationCandidateGroup {
  ruleId: string
  groupName: string | null
  memberIds: string[]
  continuation: {
    firstShift: string
    continuationShift: string
    handoverTime: string | null
    firstCredit: number
    continuationCredit: number
    totalCredit: number
    extendedEndAt: string
    dayCloseAt: string
  }
  candidates: ContinuationCandidate[]
}

export interface ContinuationCandidatesResponse {
  shiftDay: string
  ruleId: string | null
  groups: ContinuationCandidateGroup[]
  rotationWeekdays?: number[]
}

export function fetchContinuationCandidates({
  shiftDay,
}: {
  shiftDay: string
}) {
  return apiClient.get<ContinuationCandidatesResponse>(
    `/attendances/continuation-candidates`,
    {
      params: { shiftDay },
    }
  )
}

export function markContinuations({
  body,
}: {
  body: { ruleId: string; shiftDay: string; attendanceIds: string[] }
}) {
  return apiClient.post<{
    updated: number
    updatedIds: string[]
    skipped: { attendanceId: string; reason: string }[]
  }>(`/attendances/continuations`, body)
}

// Day Snapshot — vehicle events (idle flags, driver changes, moves). These live
// beside the reading API: nothing here touches /readings.
export function fetchVehicleEvents({
  readingIds,
}: {
  readingIds: Array<string>
}) {
  return apiClient.get<{ results: Array<VehicleEvent> }>(`/vehicle-events`, {
    params: {
      readingIds: readingIds.join(','),
    },
  })
}

export type VehicleEventWrite =
  | { type: 'IDLE'; readingId: string; comment: string }
  | { type: 'IDLE_RESOLVED'; readingId: string }
  | {
      type: 'DRIVER_CHANGED'
      readingId: string
      fromEmployeeId: string
      toEmployeeId: string
      comment: string
    }
  | {
      type: 'MOVE'
      readingId?: string
      vehicleId?: string
      comment?: string | null
    }

export function createVehicleEvent({ body }: { body: VehicleEventWrite }) {
  return apiClient.post(`/vehicle-events`, { ...body })
}

export function fetchMovePrecheck({
  vehicleId,
  employeeId,
  workOrderId,
}: {
  vehicleId: string
  employeeId: string
  workOrderId?: string
}) {
  return apiClient.get<MovePrecheck>(`/vehicle-events/move-precheck`, {
    params: { vehicleId, employeeId, workOrderId },
  })
}
